import { User } from 'lucide-react';
import { Section, Field, EditButton, EditActions } from '../ui';
import { ic, formatDOBInput, formatDate } from '../helpers';
import { STATES, PREFIXES, SUFFIXES } from '../constants';

// ─────────────────────────────────────────
// CLIENT INFO SECTION
// Personal details + driver questions + spouse/domestic partner panel.
// All editing happens against `basicDraft` owned by the parent.
// ─────────────────────────────────────────
export default function ClientInfoSection({
  client,
  users,
  editing,
  setEditing,
  basicDraft,
  setBasicDraft,
  onSave,
  saving,
}) {
  const userOptions = users.map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` }));

  const onCancel = () => {
    setBasicDraft({
      ...client,
      dateOfBirth: formatDate(client.dateOfBirth) || '',
      spouseDOB: formatDate(client.spouseDOB) || '',
    });
    setEditing(false);
  };

  return (
    <Section
      title="Client Information"
      icon={User}
      action={editing
        ? <EditActions onSave={onSave} onCancel={onCancel} saving={saving} />
        : <EditButton onClick={() => setEditing(true)} />}
    >
      <div className="grid grid-cols-2 gap-4 pt-3">
        <Field label="Last Name" name="lastName"
          value={editing ? basicDraft.lastName : client.lastName}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, lastName: e.target.value }))} />
        <Field label="First Name" name="firstName"
          value={editing ? basicDraft.firstName : client.firstName}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, firstName: e.target.value }))} />
        <Field label="Email" name="email" type="email"
          value={editing ? basicDraft.email : client.email}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, email: e.target.value }))} />
        <Field label="Phone" name="phone"
          value={editing ? basicDraft.phone : client.phone}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, phone: e.target.value }))} />
        <Field label="Date of Birth" name="dateOfBirth" type="text" isDate={!editing}
          value={editing ? basicDraft.dateOfBirth : client.dateOfBirth}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, dateOfBirth: formatDOBInput(e.target.value) }))} />
        <Field label="Marital Status" name="maritalStatus"
          value={editing ? basicDraft.maritalStatus : client.maritalStatus}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, maritalStatus: e.target.value }))}
          options={['Single','Married','Divorced','Widowed','Domestic Partner']} />
        <Field label="Gender" name="gender"
          value={editing ? basicDraft.gender : client.gender}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, gender: e.target.value }))}
          options={['Male','Female','Non-binary','Prefer not to say']} />
        <Field label="Driver's License #" name="driversLicenseNumber"
          value={editing ? basicDraft.driversLicenseNumber : client.driversLicenseNumber}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, driversLicenseNumber: e.target.value }))} />
        <Field label="License State" name="driversLicenseState"
          value={editing ? basicDraft.driversLicenseState : client.driversLicenseState}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, driversLicenseState: e.target.value }))}
          options={STATES} />
        <Field label="Employer" name="employer"
          value={editing ? basicDraft.employer : client.employer}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, employer: e.target.value }))} />
        <Field label="Occupation" name="occupation"
          value={editing ? basicDraft.occupation : client.occupation}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, occupation: e.target.value }))} />
        <Field label="Producer" name="producer_id"
          value={editing ? basicDraft.producer_id : client.producer_name}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, producer_id: e.target.value }))}
          options={userOptions} />
        <Field label="Account Executive" name="account_executive_id"
          value={editing ? basicDraft.account_executive_id : client.account_executive_name}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, account_executive_id: e.target.value }))}
          options={userOptions} />
        <Field label="Prefix" name="prefix"
          value={editing ? basicDraft.prefix : client.prefix}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, prefix: e.target.value }))}
          options={PREFIXES} />
        <Field label="Middle Initial" name="middleInitial"
          value={editing ? basicDraft.middleInitial : client.middleInitial}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, middleInitial: e.target.value }))} />
        <Field label="Suffix" name="suffix"
          value={editing ? basicDraft.suffix : client.suffix}
          editing={editing}
          onChange={e => setBasicDraft(p => ({ ...p, suffix: e.target.value }))}
          options={SUFFIXES} />
      </div>

      {/* ── Driver Questions ── */}
      <div className="mt-4 border border-slate-200 rounded-xl overflow-hidden">
        <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200">
          <p className="text-sm font-semibold text-slate-700">Driver Questions</p>
        </div>
        <div className="divide-y divide-slate-100 px-4">
          <div className="flex items-center justify-between py-2.5">
            <span className="text-sm text-slate-600">Spends 3+ months out of state per year?</span>
            {editing ? (
              <select
                value={basicDraft.spendMonthsOutOfState ? 'Yes' : 'No'}
                onChange={e => setBasicDraft(p => ({
                  ...p,
                  spendMonthsOutOfState: e.target.value === 'Yes',
                  takesAutosOutOfState: e.target.value === 'No' ? false : p.takesAutosOutOfState,
                }))}
                className="text-sm border border-slate-300 rounded-lg px-2 py-1 outline-none focus:border-blue-500 bg-white"
              >
                <option>No</option><option>Yes</option>
              </select>
            ) : (
              <span className={`text-sm font-medium px-2 py-0.5 rounded-lg ${client.spendMonthsOutOfState ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                {client.spendMonthsOutOfState ? 'Yes' : 'No'}
              </span>
            )}
          </div>
          {(editing ? basicDraft.spendMonthsOutOfState : client.spendMonthsOutOfState) && (
            <div className="flex items-center justify-between py-2.5 pl-4">
              <span className="text-sm text-slate-600">Takes autos out of state?</span>
              {editing ? (
                <select
                  value={basicDraft.takesAutosOutOfState ? 'Yes' : 'No'}
                  onChange={e => setBasicDraft(p => ({ ...p, takesAutosOutOfState: e.target.value === 'Yes' }))}
                  className="text-sm border border-slate-300 rounded-lg px-2 py-1 outline-none focus:border-blue-500 bg-white"
                >
                  <option>No</option><option>Yes</option>
                </select>
              ) : (
                <span className={`text-sm font-medium px-2 py-0.5 rounded-lg ${client.takesAutosOutOfState ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                  {client.takesAutosOutOfState ? 'Yes' : 'No'}
                </span>
              )}
            </div>
          )}
          <div className="flex items-center justify-between py-2.5">
            <span className="text-sm text-slate-600">Licensed in another state past 2 years?</span>
            {editing ? (
              <select
                value={basicDraft.licensedAnotherState ? 'Yes' : 'No'}
                onChange={e => setBasicDraft(p => ({
                  ...p,
                  licensedAnotherState: e.target.value === 'Yes',
                  priorLicenseState: e.target.value === 'No' ? '' : p.priorLicenseState,
                  priorLicenseNumber: e.target.value === 'No' ? '' : p.priorLicenseNumber,
                }))}
                className="text-sm border border-slate-300 rounded-lg px-2 py-1 outline-none focus:border-blue-500 bg-white"
              >
                <option>No</option><option>Yes</option>
              </select>
            ) : (
              <span className={`text-sm font-medium px-2 py-0.5 rounded-lg ${client.licensedAnotherState ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                {client.licensedAnotherState ? 'Yes' : 'No'}
              </span>
            )}
          </div>
          {(editing ? basicDraft.licensedAnotherState : client.licensedAnotherState) && (
            <div className="py-3 grid grid-cols-2 gap-3 pl-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">Prior License State</p>
                {editing ? (
                  <select
                    value={basicDraft.priorLicenseState || ''}
                    onChange={e => setBasicDraft(p => ({ ...p, priorLicenseState: e.target.value }))}
                    className="w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5 outline-none focus:border-blue-500 bg-white"
                  >
                    <option value="">Select...</option>
                    {STATES.map(s => <option key={s}>{s}</option>)}
                  </select>
                ) : (
                  <p className="text-sm font-medium text-slate-800">{client.priorLicenseState || '—'}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Prior License Number</p>
                {editing ? (
                  <input
                    value={basicDraft.priorLicenseNumber || ''}
                    onChange={e => setBasicDraft(p => ({ ...p, priorLicenseNumber: e.target.value }))}
                    placeholder="License number"
                    className="w-full text-sm border border-slate-300 rounded-lg px-2 py-1.5 outline-none focus:border-blue-500 bg-white"
                  />
                ) : (
                  <p className="text-sm font-medium text-slate-800">{client.priorLicenseNumber || '—'}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Spouse / Domestic Partner ── */}
      {(client.maritalStatus === 'Married' || client.maritalStatus === 'Domestic Partner') && (
        <div className="mt-4 border border-blue-200 rounded-xl overflow-hidden">
          <div className="bg-blue-50 px-4 py-2.5 border-b border-blue-200">
            <p className="text-sm font-semibold text-blue-800">
              {client.maritalStatus === 'Domestic Partner' ? 'Domestic Partner' : 'Spouse'}
            </p>
          </div>
          <div className="p-4 grid grid-cols-2 gap-3">
            <Field label="Prefix" name="spousePrefix"
              value={editing ? basicDraft.spousePrefix : client.spousePrefix}
              editing={editing}
              onChange={e => setBasicDraft(p => ({ ...p, spousePrefix: e.target.value }))}
              options={PREFIXES} />
            <Field label="First Name" name="spouseFirstName"
              value={editing ? basicDraft.spouseFirstName : client.spouseFirstName}
              editing={editing}
              onChange={e => setBasicDraft(p => ({ ...p, spouseFirstName: e.target.value }))} />
            <Field label="Middle Initial" name="spouseMiddleInitial"
              value={editing ? basicDraft.spouseMiddleInitial : client.spouseMiddleInitial}
              editing={editing}
              onChange={e => setBasicDraft(p => ({ ...p, spouseMiddleInitial: e.target.value }))} />
            <Field label="Last Name" name="spouseLastName"
              value={editing ? basicDraft.spouseLastName : client.spouseLastName}
              editing={editing}
              onChange={e => setBasicDraft(p => ({ ...p, spouseLastName: e.target.value }))} />
            <Field label="Suffix" name="spouseSuffix"
              value={editing ? basicDraft.spouseSuffix : client.spouseSuffix}
              editing={editing}
              onChange={e => setBasicDraft(p => ({ ...p, spouseSuffix: e.target.value }))}
              options={SUFFIXES} />
            <Field label="Date of Birth" name="spouseDOB" type="text" isDate={!editing}
              value={editing ? basicDraft.spouseDOB : client.spouseDOB}
              editing={editing}
              onChange={e => setBasicDraft(p => ({ ...p, spouseDOB: formatDOBInput(e.target.value) }))} />
          </div>
        </div>
      )}
    </Section>
  );
}